import React, { useState, useRef, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowDown, Download, Github, Linkedin, ExternalLink, Mail } from 'lucide-react';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import ParticleField from './ParticleField';
import TypewriterText from './TypewriterText';
import ProfileImage from './ProfileImage';
import { personalInfo } from '../data/portfolio';
import { unlockEgg } from '@/lib/utils';
import { useSystemMode } from '@/context/SystemModeContext';

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.15, delayChildren: 0.3 }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: 'easeOut' } }
};

const HeroSection = () => {

  const { setIsOverdrive, setIsDebug } = useSystemMode();

useEffect(() => {
  const PANEL_ID = "debug-panel";

  const createDebugPanel = () => {
    if (document.getElementById(PANEL_ID)) return;

    const panel = document.createElement("div");
    panel.id = PANEL_ID;

    panel.className = `
      fixed top-24 right-6
      text-xs font-mono
      px-4 py-3
      glass rounded-lg
      shadow-lg
      z-[999]
      isolate
      backdrop-blur-0
    `;

    document.body.appendChild(panel);
  };

  const removeDebugPanel = () => {
    const existing = document.getElementById(PANEL_ID);
    if (existing) existing.remove();
  };

  const syncPanelWithState = () => {
    const isDebug = document.documentElement.classList.contains("debug-mode");

    if (isDebug) {
      createDebugPanel();
    } else {
      removeDebugPanel();
    }
  };

  // Run once immediately (important!)
  syncPanelWithState();

  const observer = new MutationObserver(syncPanelWithState);

  observer.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ["class"],
  });

  return () => {
    observer.disconnect();
    removeDebugPanel();
  };
}, []);

  useEffect(() => {
  const sequence = [
    'ArrowUp','ArrowUp','ArrowDown','ArrowDown',
    'ArrowLeft','ArrowRight','ArrowLeft','ArrowRight','b','a'
  ];

  let index = 0;

  const handler = (e) => {
    if (e.key.toLowerCase() === sequence[index].toLowerCase()) {
      index++;
      if (index === sequence.length) {
        const discovered = JSON.parse(localStorage.getItem("easter-eggs") || "[]");
        if (!discovered.includes("konami")) {
                unlockEgg("konami");
        }
        setIsDebug(prev => !prev);
        if(document.documentElement.classList.contains('debug-mode')){
          document.documentElement.classList.remove('debug-mode');
        }else{
          document.documentElement.classList.add('debug-mode');
        }
        index = 0;
      }
    } else {
      index = 0;
    }
  };

  window.addEventListener('keydown', handler);
  return () => window.removeEventListener('keydown', handler);
});

useEffect(() => {
  const handler = () => {
    const toast = document.createElement("div");
    toast.style.border = "1px solid hsl(var(--primary) / 0.4)";
    const messages = [
      "Neural Core: Nominal • Latency: 12ms • Overthinking: Active",
      "Runtime: 7+ Years • Edge Cases: Embraced • Ego: Stable",
      "System Stable • Coffee Reserves: Sufficient",
      "Interaction Threshold Reached • Curiosity Confirmed",
      "Recruiter Detected • Conversation Recommended",
    ];
    const discovered = JSON.parse(localStorage.getItem("easter-eggs") || "[]");
    if (!discovered.includes("photo")) {
                unlockEgg("photo");
        }
    toast.innerText = messages[Math.floor(Math.random() * messages.length)];
    toast.className =
      "fixed bottom-6 right-6 px-4 py-2 text-xs font-mono glass rounded-lg shadow-lg z-50";
    document.body.appendChild(toast);
    setTimeout(() => toast.remove(), 4000);
  };

  window.addEventListener("diagnostic-mode", handler);
  return () => window.removeEventListener("diagnostic-mode", handler);
}, []);

useEffect(() => {
  const sequence = ["r","a","j","i","b"];
  let index = 0;

  const handler = (e) => {
    const tag = document.activeElement?.tagName;
      const isTyping = tag === "INPUT" || tag === "TEXTAREA" || document.activeElement?.isContentEditable;
      if (isTyping) return;
    if (e.key.toLowerCase() === sequence[index]) {
      index++;
      if (index === sequence.length) {
        setIsOverdrive(prev => !prev);
        const discovered = JSON.parse(localStorage.getItem("easter-eggs") || "[]");
        if (!discovered.includes("rajib")) {
                unlockEgg("rajib");
        }
        document.documentElement.classList.add("flash");

        setTimeout(() => {
          document.documentElement.classList.remove("flash");
        }, 300);
        const toast = document.createElement("div");
        toast.style.border = "1px solid hsl(var(--primary) / 0.4)";
        if(document.documentElement.classList.contains('overdrive')){
          document.documentElement.classList.remove('overdrive');
          toast.innerText = "OVERDRIVE: OFF";
        }else{
          document.documentElement.classList.add('overdrive');
          toast.innerText = "OVERDRIVE: ON";
        }
         
        
        toast.className = "fixed top-6 right-6 text-xs font-mono px-3 py-1 glass rounded-md z-50";
        document.body.appendChild(toast);
        setTimeout(() => toast.remove(), 4000);
        index = 0;
      }
    } else {
      index = 0;
    }
  };

  window.addEventListener("keydown", handler);
  return () => window.removeEventListener("keydown", handler);
});



  return (
    <section
      id="hero"
      className="relative min-h-screen flex items-center overflow-hidden"
    >
      {/* Background layers */}
        <div className="absolute inset-0 pointer-events-none">
          {/* <div className="absolute inset-0 bg-background dark:bg-background" /> */}
          {/* <div className="absolute inset-0 bg-gradient-to-b from-transparent to-black/5 dark:to-black/40" /> */}
        </div>

      {/* Radial glow effects */}
      <div
        className="absolute top-1/4 left-1/3 w-[600px] h-[600px] rounded-full pointer-events-none hidden dark:block"
        style={{
          background: 'radial-gradient(circle, hsl(var(--primary) / 0.07) 0%, transparent 70%)',
        }}
      />
      <div
        className="absolute bottom-1/4 right-1/4 w-[400px] h-[400px] rounded-full pointer-events-none hidden dark:block"
        style={{
          background: 'radial-gradient(circle, hsl(var(--secondary) / 0.05) 0%, transparent 70%)',
        }}
      />

      <div
        className="absolute right-[15%] top-1/2 w-[450px] h-[450px] rounded-full pointer-events-none block dark:hidden"
        style={{
          background: 'radial-gradient(circle, hsl(var(--primary) / 0.08) 0%, transparent 65%)',
          transform: 'translateY(-50%)'
        }}
      />

      {/* Grid dots */}
      <div className="absolute inset-0 bg-grid pointer-events-none dark:opacity-40 opacity-15" />

      {/* Particles */}
      <div className="absolute inset-0">
        <ParticleField />
      </div>

      {/* Content */}
      <div className="relative z-10 w-full max-w-7xl mx-auto px-6 md:px-12 pt-24 pb-16">
        <div className="flex flex-col-reverse lg:flex-row items-center justify-between gap-12 lg:gap-16">

          {/* Left: Text content */}
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="flex-1 max-w-2xl"
          >
            {/* Badge */}
            <motion.div variants={itemVariants} className="mb-6">
              <Badge
                variant="outline"
                className="border-primary/30 text-primary bg-primary/5 px-4 py-1.5 text-xs font-mono tracking-wider uppercase"
              >
                <span className="w-2 h-2 rounded-full bg-accent mr-2 inline-block animate-pulse-glow" />
                Available for opportunities
              </Badge>
            </motion.div>

            {/* Main heading */}
            <motion.h1
              variants={itemVariants}
              className="font-heading font-bold leading-tight tracking-tight mb-4"
              style={{ fontSize: 'clamp(2.2rem, 5vw, 4rem)' }}
            >
              <span className="text-foreground font-extrabold">Hi, I'm{' '}</span>
              <span className="gradient-text">{personalInfo.name}</span>
            </motion.h1>

            {/* Typewriter */}
            <motion.div
              variants={itemVariants}
              className="text-2xl md:text-3xl font-heading mb-6 min-h-[44px]"
            >
              <TypewriterText />
            </motion.div>

            {/* Bio */}
            <motion.p
              variants={itemVariants}
              className="text-muted-foreground text-base leading-relaxed mb-8"
            >
              {personalInfo.bio}
            </motion.p>

            {/* Stat pills */}
            <motion.div variants={itemVariants} className="flex flex-wrap gap-3 mb-10">
              {[
                { label: '7+', sub: 'Years Exp.' },
                { label: 'MTech', sub: 'AI @ IISc' },
                { label: 'Tech Lead', sub: 'Wipro Limited' },
              ].map((stat, i) => (
                <div
                  key={i}
                  className="glass rounded-xl px-4 py-3 text-center min-w-[80px]"
                >
                  <div className="font-heading font-bold text-lg gradient-text">{stat.label}</div>
                  <div className="text-muted-foreground text-xs mt-0.5">{stat.sub}</div>
                </div>
              ))}
            </motion.div>

            {/* CTA Buttons */}
            <motion.div variants={itemVariants} className="flex flex-wrap gap-4 items-center">
              <Button
                size="lg"
                className="bg-primary text-primary-foreground hover:bg-primary/90 font-semibold px-8 shadow-md hover:shadow-lg"
                style={{ boxShadow: 'var(--shadow-primary)' }}
                onClick={() => document.querySelector('#projects')?.scrollIntoView({ behavior: 'smooth' })}
              >
                View My Work
                <ExternalLink size={16} className="ml-2" />
              </Button>
              <Button
                variant="outline"
                size="lg"
                className="border-border hover:border-primary/50 hover:text-primary text-foreground hover:bg-primary/5 px-8"
                onClick={() => document.querySelector('#contact')?.scrollIntoView({ behavior: 'smooth' })}
              >
                Get In Touch
                <Mail size={16} className="ml-2" />
              </Button>
              <Button
                variant="outline"
                size="lg"
                className="border-primary/30 text-primary hover:bg-primary/10 hover:text-foreground hover:border-primary/60 font-medium"
            
                asChild
              >
                <a href={personalInfo.resume} target="_blank" rel="noopener noreferrer">
                  <Download size={16} className="mr-2" />
                  Resume
                </a>
              </Button>
              <div className="flex items-center gap-3">
                <a
                  href="https://github.com/bboyrajib"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 glass rounded-lg flex items-center justify-center text-muted-foreground hover:text-primary transition-colors duration-200"
                >
                  <Github size={18} />
                </a>
                <a
                  href="https://linkedin.com/in/rajib-roy"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-10 h-10 glass rounded-lg flex items-center justify-center text-muted-foreground hover:text-primary transition-colors duration-200"
                >
                  <Linkedin size={18} />
                </a>
              </div>
            </motion.div>

            {/* Tech stack pills */}
            <motion.div variants={itemVariants} className="flex flex-wrap gap-2 mt-10">
              {[
  'Python',
  'PyTorch',
  'LLMs',
  'RAG',
  'React',
  'Node.js',
  'Java',
  'Docker',
  'Kubernetes',
  'CI/CD',
  'Azure DevOps',
  'Microservices',
  'Selenium',
  'BDD (Cucumber)',
  'REST Assured',
  'API Testing'
].map((tech) => (
                <span
                  key={tech}
                  className="font-mono text-xs px-3 py-1 rounded-full border border-border/60 text-muted-foreground hover:border-primary/40 hover:text-primary transition-colors duration-200"
                >
                  {tech}
                </span>
              ))}
            </motion.div>
          </motion.div>

          {/* Right: Profile image */}
          <motion.div
            initial={{ opacity: 0, scale: 0.8, x: 40 }}
            animate={{ opacity: 1, scale: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.4, ease: 'easeOut' }}
            className="flex-shrink-0 flex flex-col items-center gap-4"
          >
            <div className="relative">
              <div
                className="absolute inset-0 blur-3xl opacity-60 dark:hidden"
                style={{
                  background: 'radial-gradient(circle, hsl(var(--primary) / 0.18), transparent 70%)'
                }}
              />
              <ProfileImage size="lg" />
            </div>
          </motion.div>

        </div>
      </div>
              
      {/* Scroll indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 2, duration: 0.8 }}
        className="absolute bottom-2 lg:bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2"
      >
        <span className="text-muted-foreground text-xs font-mono">scroll</span>
        <motion.div
          animate={{ y: [0, 6, 0] }}
          transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut' }}
        >
          <ArrowDown size={16} className="text-primary" />
        </motion.div>
      </motion.div>
    </section>
  );
};

export default HeroSection;
